import { ofetch } from "ofetch";
import { ref, inject, hasInjectionContext } from "vue";
import { useSettingsStore } from "@/stores/useSettingsStore";
import { useMessageStore } from "../stores/useMessageStore";
import { useLoadingStore } from "../stores/useLoadingStore";
import { defaultWindow, defaultDocument } from "@vueuse/core";

export function createOfetch() {
  const settingsStore = useSettingsStore();
  const messageStore = useMessageStore();

  const _ofetch = ofetch.create({
    baseURL: import.meta.env.VUE_APP_API_BASE_PATH || "/api",
    credentials: "same-origin",

    async onRequest({ options }) {
      // Check if URL starts with /
      if (options.path && options.path[0] !== "/") {
        console.warn(`Missing '/' in url: ${options.path}`);
      }
      const lang = defaultDocument?.documentElement?.lang;
      if (lang) {
        options.headers = new Headers(options.headers);
        options.headers.set("content-language", lang);
      }
    },

    async onRequestError({ error }) {
      console.error("Request error:", error?.message || error);
      messageStore.addI18nMessage("error", "errors.server.unexpected");
      return Promise.reject(error);
    },

    async onResponse({ response }) {
      if (response._data) {
        return response._data;
      }
      return response;
    },

    async onResponseError({ response, error }) {
      const status = response?.status;
      const errorData = response?._data?.error || response?._data || error;

      if (status === 401) {
        if (console && console.log) console.log("401 Unauthorized", errorData);
        window.location.replace(`${settingsStore.settings.loginUrl}?redirectUrl=${window.location.href}`);
      } else if (status === 400) {
        if (console && console.log) console.log("Bad request", errorData);
        // Une ValidationError Sequelize arrive en tableau (`server.js` la sérialise par champ) : sans
        // aplatissement, toute erreur de validation base s'affichait comme un « vérifiez les valeurs »
        // sans le moindre détail, y compris dans les écrans d'administration.
        const details = Array.isArray(errorData) ? errorData : [errorData];
        const appError = details.find((d) => d && d.name === "AppError");
        const technical = details.map((d) => d && d.message).filter(Boolean);
        if (appError) {
          messageStore.addI18nMessage("error", appError.message, appError.params);
        } else if (technical.length) {
          messageStore.addError(technical.join(" — "));
        } else {
          messageStore.addI18nMessage("error", "errors.server.validation");
        }
      } else if (status === 403) {
        if (console && console.log) console.log("Unauthorized", errorData);
        window.location.href = settingsStore.settings.myProfile;
      } else if (status === 404) {
        if (console && console.log) console.log("Not found", errorData);
      } else if (status === 500) {
        if (console && console.log) console.log("Technical error", errorData);
        messageStore.addI18nMessage("error", "errors.server.technical");
      } else {
        if (console && console.log) console.log("Unexpected error", status, errorData);
        messageStore.addI18nMessage("error", "errors.server.unexpected");
      }

      if (errorData instanceof Error) return Promise.reject(errorData);
      return Promise.reject(Object.assign(new Error(errorData?.message || `HTTP ${status}`), errorData));
    },
  });

  _ofetch["install"] = function (app) {
    app.provide("ofetch", this);
    if (defaultWindow) defaultWindow["ofetch"] = this;
  };

  return _ofetch;
}

/**
 * @param {(args: any[]) => Parameters<import("ofetch").ofetch>} optionsGenerator
 * @returns {FetchAPI<any>}
 */
export function createAPIHandler(optionsGenerator) {
  const _fetch = async (args) => await ((hasInjectionContext() && inject("ofetch")) || defaultWindow["ofetch"])(...optionsGenerator(args));
  // @ts-ignore
  return Object.assign((...args) => _fetch(args), {
    ref: function (defaultValue) {
      const _ref = Object.assign(ref(defaultValue), {
        async fetch(...args) {
          let _onFulFilled, _onRejection, _onFinally;
          try {
            const result = await _fetch(args);
            _ref.value = result;
            if (_onFulFilled) _onFulFilled(result);
          } catch (error) {
            if (_onRejection) _onRejection(error);
          } finally {
            if (_onFinally) _onFinally();
          }
          return Object.assign(_ref, {
            then: (onFulFilled, onRejection) => {
              _onFulFilled = onFulFilled;
              if (onRejection) _onRejection = onRejection;
              return _ref;
            },
            finally: (onFinally) => {
              _onFinally = onFinally;
              return _ref;
            },
            catch: (onRejection) => {
              _onRejection = onRejection;
              return _ref;
            },
          });
        },
      });
      return _ref;
    },
  });
}

/**
 * Raw fetch returning the full ofetch response (status, headers, _data). Used by
 * generated `$download` clients to read the blob body and Content-Disposition.
 * @param {string} url
 * @param {import("ofetch").FetchOptions} options
 */
export const useOFetchRaw = async function (url, options) {
  const loadingStore = useLoadingStore();
  loadingStore.setLoading(true);
  try {
    return await ((hasInjectionContext() && inject("ofetch")) || defaultWindow["ofetch"]).raw(url, options);
  } finally {
    loadingStore.setLoading(false);
  }
};
